import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getTaskTypes,
  createTaskType,
  updateTaskType,
  deleteTaskType,
} from '../api/task-typeAPI';
import { fetchData } from '../utils/apiHelpers';

const TaskTypeListLayer = () => {
  const userProfile = useUserProfile();
  const [taskTypeList, setTaskTypeList] = useState([]);
  const [newTaskType, setNewTaskType] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchTaskTypes = () => {
    fetchData(getTaskTypes, {}, setTaskTypeList);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newTaskType.taskTypeName?.trim()) newErrors.taskTypeName = 'Task Type Name is required';
    if (!newTaskType.taskTypeCode?.trim()) newErrors.taskTypeCode = 'Task Type Code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchTaskTypes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (taskTypeList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [taskTypeList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (type === 'checkbox') {
      newValue = checked ? 1 : 0;
    } else if (type === 'select-one' && (value === '0' || value === '1')) {
      newValue = Number(value);
    }

    setNewTaskType((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleEdit = (item) => {
    setNewTaskType(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task type?')) return;
    try {
      await deleteTaskType(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchTaskTypes();
    } catch (error) {
      console.error('Error deleting task type:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const cleanPayload = {};
      const numericToggleFields = ['component', 'service', 'defaultTaskType'];
      for (const key in newTaskType) {
        if (newTaskType[key] !== null && typeof newTaskType[key] !== 'object') {
          if (numericToggleFields.includes(key)) {
            cleanPayload[key] = newTaskType[key] === 1 || newTaskType[key] === true ? 1 : 0;
          } else {
            cleanPayload[key] = newTaskType[key];
          }
        }
      }

      let savedTaskTypeId = editingId;

      if (editingId) {
        await updateTaskType(editingId, cleanPayload);
      } else {
        const createdTaskType = await createTaskType(cleanPayload);
        savedTaskTypeId = createdTaskType?.id || null;
      }

      const isDefaultSelected = cleanPayload.defaultTaskType === 1 || cleanPayload.defaultTaskType === true;

      if (isDefaultSelected) {
        const defaultResetPromises = taskTypeList
          .filter(
            (taskType) =>
              (!savedTaskTypeId || taskType.id !== savedTaskTypeId) &&
              (taskType.defaultTaskType === 1 || taskType.defaultTaskType === true)
          )
          .map((taskType) => updateTaskType(taskType.id, { defaultTaskType: 0 }));

        await Promise.all(defaultResetPromises);
      }

      setShowModal(false);
      setEditingId(null);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      setNewTaskType({});
      await fetchTaskTypes();
    } catch (error) {
      console.error('Error saving task type:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('task-type:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewTaskType({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Task Type
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Description</th>
                <th>Component</th>
                <th>Service</th>
                <th>Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taskTypeList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.taskTypeName}</strong></td>
                  <td>{item.taskTypeCode}</td>
                  <td>{item.taskTypeDescription}</td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`component-switch-${item.id}`}
                        checked={item.component === 1}
                        disabled
                      />
                      <label className="form-check-label" htmlFor={`component-switch-${item.id}`}>
                        {item.component === 1 ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`service-switch-${item.id}`}
                        checked={item.service === 1 || item.service === true}
                        disabled
                      />
                      <label className="form-check-label" htmlFor={`service-switch-${item.id}`}>
                        {item.service === 1 || item.service === true ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`default-switch-${item.id}`}
                        checked={item.defaultTaskType === 1 || item.defaultTaskType === true}
                        disabled
                      />
                      <label className="form-check-label" htmlFor={`default-switch-${item.id}`}>
                        {item.defaultTaskType === 1 || item.defaultTaskType === true ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('task-type:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('task-type:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Task Type' : 'New Task Type'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.taskTypeName ? 'is-invalid' : ''}`}
                      name="taskTypeName"
                      value={newTaskType.taskTypeName || ''}
                      onChange={handleInputChange}
                    />
                    {errors.taskTypeName && <div className="invalid-feedback">{errors.taskTypeName}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Code *</label>
                    <input
                      className={`form-control ${errors.taskTypeCode ? 'is-invalid' : ''}`}
                      name="taskTypeCode"
                      value={newTaskType.taskTypeCode || ''}
                      onChange={handleInputChange}
                    />
                    {errors.taskTypeCode && <div className="invalid-feedback">{errors.taskTypeCode}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label d-block">Default</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="defaultTaskType"
                        checked={newTaskType.defaultTaskType === 1 || newTaskType.defaultTaskType === true}
                        onChange={handleInputChange}
                      />
                      <span className="form-check-label">
                        {newTaskType.defaultTaskType === 1 || newTaskType.defaultTaskType === true ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label d-block">Component</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="component"
                        checked={newTaskType.component === 1 || newTaskType.component === true}
                        onChange={handleInputChange}
                      />
                      <span className="form-check-label">
                        {newTaskType.component === 1 || newTaskType.component === true ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label d-block">Service</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="service"
                        checked={newTaskType.service === 1 || newTaskType.service === true}
                        onChange={handleInputChange}
                      />
                      <span className="form-check-label">
                        {newTaskType.service === 1 || newTaskType.service === true ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="taskTypeDescription"
                      value={newTaskType.taskTypeDescription || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTypeListLayer;
